import { defaultGuid } from '@equifinance/shared';
import { useCallback, useState } from 'react';
import useAccountApi, { WithdrawReasonType } from '../../api/useAccountApi';
import { Stages } from '../../types/Stages';
import { Applicant } from '../account/Account';
import { Case, CaseBrokerStatuses, CaseStatuses } from '.';

export const defaultCase: Omit<Case, 'errors'> = {
  id: defaultGuid,
  reference: '',
  applicantNames: '',
  securityAddressPostCode: '',
  brokerStatus: CaseBrokerStatuses.CREDIT_DECISION,
  status: CaseStatuses.WITH_BROKER,
  brokerageName: '',
  brokerageId: '',
  brokerAssignedToId: '',
  brokerAssignedToName: '',
  isCurrentBroker: false,
  underwriterAssignedToName: '',
  totalTasksOutstanding: 0,
  lastUpdated: '',
  rejectionReason: '',
  withdrawalReason: '',
  otherWithdrawalDetail: '',
};

export const useCases = () => {
  const [cases, setCases] = useState<Case[]>([]);

  const {
    postSubmitAccount,
    getCases,
    postUpdateCase,
    postDeleteCase,
    putWithdrawCase,
    putReopenCase,
    getWithdrawalReasons,
    putUpdateBrokerOwner,
  } = useAccountApi();

  const updateCase = useCallback((id: string, data: Partial<Case>) => {
    setCases((prev) => prev.map((item) => (item.id === id ? { ...item, ...data } : item)));
  }, []);

  return {
    cases,
    setCasesData: setCases,
    async fetchCases() {
      const cases = (await getCases()) || [];
      setCases(cases);
    },
    async createCase() {
      const account = await postSubmitAccount({
        stage: Stages.CreditDecision,
        applicants: [{} as Applicant],
        advisorDetails: {
          id: defaultGuid,
          wasResponsibleForAdvice: true,
        },
      });
      const newCase: Case = { ...defaultCase, id: account.id };
      setCases((prev) => ([
        ...prev,
        newCase,
      ]));
      return account;
    },
    async updateCase(data: Partial<Case>, id: string) {
      updateCase(id, data);
      await postUpdateCase(data);
    },
    async updateBrokerOwner(id: string, ownerId: string, ownerName: string | null) {
      const preUpdateCase = cases.find((c) => c.id === id)!;
      updateCase(id, { brokerAssignedToId: ownerId, brokerAssignedToName: ownerName || '', errors: {} });
      const response = await putUpdateBrokerOwner(id, ownerId, ownerName);
      if (response.errors) {
        updateCase(id, { ...preUpdateCase, ...response });
      }
      return response;
    },
    async deleteCase(id: string) {
      setCases((prev) => prev.filter((item) => item.id !== id));
      await postDeleteCase(id);
    },
    async getWithdrawalReasons() {
      return getWithdrawalReasons();
    },
    async withdrawCase(id: string, withdrawReason: WithdrawReasonType) {
      const response = await putWithdrawCase(id, withdrawReason);
      if (typeof response !== 'string' && response.id) {
        updateCase(id, { ...response });
      }
      return response;
    },
    async reopenCase(id: string) {
      const response = await putReopenCase(id);
      if (typeof response !== 'string' && response.id) {
        updateCase(id, { ...response });
      }
      return response;
    },
  };
};
